/**
 * Fill out and submit a custom report template.
 */
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { ArrowLeft, Loader2, Send } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/lib/auth";
import { useFeatureFlags } from "@/hooks/useFeatureFlags";
import { toast } from "sonner";
import {
  getTemplate, listFields, submitReport,
  type ReportTemplate, type ReportTemplateField,
} from "@/lib/report-templates";
import {
  CustomTemplateRenderer, validateValues, type FieldValue,
} from "@/components/reports/CustomTemplateRenderer";

export const Route = createFileRoute("/_authenticated/reports/templates/$id/fill")({
  component: FillPage,
  head: () => ({ meta: [{ title: "File report — MissionReady" }] }),
});

function FillPage() {
  const { id } = Route.useParams();
  const { currentOrg, user } = useAuth();
  const flags = useFeatureFlags();
  const navigate = useNavigate();

  const [tmpl, setTmpl] = useState<ReportTemplate | null>(null);
  const [fields, setFields] = useState<ReportTemplateField[]>([]);
  const [values, setValues] = useState<Record<string, FieldValue>>({});
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [sites, setSites] = useState<Array<{ id: string; name: string }>>([]);
  const [siteId, setSiteId] = useState<string>("");
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const [t, fs] = await Promise.all([getTemplate(id), listFields(id)]);
        if (cancelled) return;
        setTmpl(t);
        setFields(fs);
        if (currentOrg) {
          const { data } = await supabase
            .from("sites")
            .select("id,name")
            .eq("organization_id", currentOrg.id)
            .order("name");
          if (!cancelled) setSites((data ?? []) as Array<{ id: string; name: string }>);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [id, currentOrg?.id]);

  const canSubmit = useMemo(() => tmpl?.status === "published", [tmpl]);

  const handleSubmit = async () => {
    if (!tmpl || !currentOrg || !user) return;
    const errs = validateValues(fields, values);
    setErrors(errs);
    if (Object.keys(errs).length > 0) {
      toast.error("Please fill in all required fields.");
      return;
    }
    setSubmitting(true);
    try {
      await submitReport({
        template_id: tmpl.id,
        organization_id: currentOrg.id,
        submitted_by: user.id,
        site_id: siteId || null,
        values: values as Record<string, unknown>,
      });
      toast.success("Report submitted");
      navigate({ to: "/reports/templates" });
    } catch (e) {
      toast.error(`Could not submit: ${String((e as Error).message ?? e)}`);
    } finally {
      setSubmitting(false);
    }
  };

  if (flags.loaded && flags.custom_reports !== true) {
    return (
      <div className="mx-auto max-w-2xl px-4 py-12">
        <p className="text-sm text-muted-foreground">Upgrade to file custom reports.</p>
        <Button asChild className="mt-3"><Link to="/reports">Back</Link></Button>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="mx-auto max-w-2xl px-4 py-12">
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading…
        </div>
      </div>
    );
  }

  if (!tmpl) {
    return (
      <div className="mx-auto max-w-2xl px-4 py-12">
        <p className="text-sm text-muted-foreground">Template not found.</p>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-2xl px-4 pb-32 pt-6 md:px-8 md:py-8">
      <Button asChild variant="ghost" size="sm" className="mb-4 -ml-2">
        <Link to="/reports/templates"><ArrowLeft className="mr-1 h-4 w-4" /> Templates</Link>
      </Button>

      <h1 className="font-display text-2xl font-bold tracking-tight md:text-3xl">{tmpl.name}</h1>
      {tmpl.description && (
        <p className="mt-1 text-sm text-muted-foreground">{tmpl.description}</p>
      )}
      {tmpl.status !== "published" && (
        <div className="mt-3 rounded-lg border border-border bg-muted/30 p-3 text-xs text-muted-foreground">
          This template is a {tmpl.status}. Publish it before submitting real reports.
        </div>
      )}

      <div className="mt-6 rounded-2xl border border-border bg-card p-4">
        {sites.length > 0 && (
          <div className="mb-5">
            <Label>Site (optional)</Label>
            <select
              value={siteId} onChange={(e) => setSiteId(e.target.value)}
              className="mt-1 h-10 w-full rounded-md border border-border bg-background px-2 text-sm"
            >
              <option value="">No site</option>
              {sites.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
          </div>
        )}

        <CustomTemplateRenderer
          fields={fields}
          values={values}
          onChange={setValues}
          errors={errors}
        />

        {fields.length === 0 && (
          <p className="py-6 text-center text-sm text-muted-foreground">This template has no fields yet.</p>
        )}
      </div>

      <div className="fixed inset-x-0 bottom-0 z-30 border-t border-border bg-card/95 px-4 py-3 backdrop-blur md:static md:mt-6 md:rounded-2xl md:border md:bg-card md:p-4">
        <div className="mx-auto flex max-w-2xl justify-end">
          <Button
            disabled={!canSubmit || submitting || fields.length === 0}
            onClick={handleSubmit}
            className="gap-2"
          >
            {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
            Submit report
          </Button>
        </div>
      </div>
    </div>
  );
}
